import { UNIT_ENGAGEMENT_CONFIG } from './config.js';
/**
 * Reports listing-card image browsing as a short unit "visit", reusing the
 * unit-page `page_active` / `page_inactive` endpoints. Platform-free like
 * `createUnitVisitTracker`: callers wire their own login-gating and API calls
 * through `onActive` / `onInactive`.
 *
 * The server owns all drop-off policy — the 5-minute grouping with a later
 * same-unit page visit, deduplication, and the schedule-tour / open-house
 * exclusion. This only reports that a browse happened; a browse and a page
 * visit both send `{ visitId }`, so the server can group them.
 *
 * Tracks one browse per unit slug (a grid shows many cards at once), so
 * browsing card A then card B opens two independent browses.
 */
export function createImageBrowseReporter(options) {
    const config = { ...UNIT_ENGAGEMENT_CONFIG, ...options.config };
    const browses = new Map();
    function clearIdle(active) {
        if (active.idleTimer !== null) {
            clearTimeout(active.idleTimer);
            active.idleTimer = null;
        }
    }
    function armIdle(unitId, active) {
        clearIdle(active);
        active.idleTimer = setTimeout(() => end(unitId), config.imageBrowseIdleMs);
    }
    function browse(unitId) {
        if (!unitId)
            return;
        const existing = browses.get(unitId);
        if (existing) {
            // Same browse continuing — just push the idle deadline out, no new report.
            armIdle(unitId, existing);
            return;
        }
        const active = { visitId: options.createVisitId(), idleTimer: null };
        browses.set(unitId, active);
        options.onActive({ unitId, visitId: active.visitId });
        armIdle(unitId, active);
    }
    function end(unitId) {
        const active = browses.get(unitId);
        if (!active)
            return;
        clearIdle(active);
        browses.delete(unitId);
        options.onInactive({ unitId, visitId: active.visitId });
    }
    function endAll() {
        // Snapshot the keys: `end` mutates the map as it reports each browse.
        for (const unitId of Array.from(browses.keys()))
            end(unitId);
    }
    function dispose() {
        for (const active of browses.values())
            clearIdle(active);
        browses.clear();
    }
    return { browse, end, endAll, dispose };
}
